import { Toast, useToast } from '@lego/connect-components-react';
import { type ReactNode, useCallback } from 'react';

type NotificationVariant = 'success' | 'error' | 'warning' | 'info';

type InnerProps = {
  type: NotificationVariant;
  title: string;
  description?: ReactNode | string;
  duration?: number;
  id?: string;
};

const VARIANT_MAP: Record<
  NotificationVariant,
  'success' | 'negative' | 'warning' | 'informative'
> = {
  success: 'success',
  error: 'negative',
  warning: 'warning',
  info: 'informative',
};

export const useNotification = () => {
  const { toast } = useToast();

  const showNotification = useCallback(
    ({ title, type, description, duration = 8_000, id }: InnerProps) => {
      const toastKey = id ?? `${type}:${title}`;

      const shouldAutoClose = duration > 0;

      const children = description ? (
        <Toast
          variant={VARIANT_MAP[type]}
          autoClose={shouldAutoClose}
          autoCloseTime={duration}
        >
          <Toast.Title>{title}</Toast.Title>
          <Toast.Content>{description}</Toast.Content>
        </Toast>
      ) : (
        <Toast
          variant={VARIANT_MAP[type]}
          autoClose={shouldAutoClose}
          autoCloseTime={duration}
        >
          <Toast.Title>{title}</Toast.Title>
        </Toast>
      );

      toast(children, { key: toastKey, replace: true });
    },
    [toast],
  );

  return showNotification;
};

export type UseNotificationType = ReturnType<typeof useNotification>;
